import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Download, ShieldCheck, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";

export default function Claim() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/claim/${token}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Not found");
        }
        setState({ loading: false, data: await res.json(), error: null });
      } catch (err) {
        setState({ loading: false, data: null, error: err.message });
      }
    })();
  }, [token]);

  const download = () => window.open(`${API}/claim/${token}/pdf`, "_blank");

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-3xl mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/imcac_logo.png" alt="IMCAC" className="h-9 w-9 object-contain" />
            <span className="font-display font-bold text-sm">IMCAC<span className="text-red-800">.</span></span>
          </Link>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">
            Your certificate
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        {state.loading && (
          <div className="text-sm text-zinc-500 font-mono" data-testid="claim-loading">Loading your certificate…</div>
        )}

        {!state.loading && state.error && (
          <div className="border-2 border-red-600 bg-red-50 rounded-lg p-8" data-testid="claim-error">
            <XCircle className="h-10 w-10 text-red-600 mb-4" strokeWidth={1.5} />
            <h2 className="font-display text-2xl font-bold text-red-900">Claim link not valid</h2>
            <p className="text-red-800 mt-2 text-sm">{state.error}</p>
            <p className="text-red-700 text-xs mt-4">
              Ask your compliance manager to re-share your link, or verify a specific certificate ID at{" "}
              <Link to="/verify" className="underline">/verify</Link>.
            </p>
          </div>
        )}

        {!state.loading && state.data && (
          <div data-testid="claim-success">
            <div className="text-xs font-mono uppercase tracking-widest text-red-600 mb-3">
              Personalized certificate
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">
              Hello, {state.data.holder_name.split(" ")[0]}. Your certificate is ready.
            </h1>
            <p className="text-zinc-600 mt-3 max-w-xl">
              Your organization has completed a bulk purchase for IMCAC Bloodborne Pathogens
              Training. Download your personal PDF below — you&rsquo;ll need to present this to your employer.
            </p>

            <div className="mt-10 bg-white rounded-lg p-8 border-2 border-emerald-600">
              <div className="flex items-start justify-between gap-6">
                <div>
                  <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 rounded-full px-3 py-1 text-xs font-mono uppercase tracking-widest">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Paid &amp; issued
                  </div>
                  <h2 className="font-display text-3xl font-bold tracking-tight mt-4">
                    {state.data.holder_name}
                  </h2>
                  <p className="font-mono text-sm text-zinc-700 mt-1" data-testid="claim-cert-id">
                    {state.data.certificate_id}
                  </p>
                </div>
                <ShieldCheck className="h-14 w-14 text-emerald-600" strokeWidth={1.2} />
              </div>

              <div className="mt-8 grid grid-cols-3 border-t border-l border-zinc-200">
                <div className="border-r border-b border-zinc-200 p-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Score</div>
                  <div className="font-display font-bold text-2xl text-red-600 mt-1">{state.data.score}%</div>
                </div>
                <div className="border-r border-b border-zinc-200 p-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Issued</div>
                  <div className="font-mono text-sm mt-1">{String(state.data.issued_at).slice(0, 10)}</div>
                </div>
                <div className="border-r border-b border-zinc-200 p-4">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Valid until</div>
                  <div className="font-mono text-sm mt-1">{String(state.data.expires_at).slice(0, 10)}</div>
                </div>
              </div>

              <Button onClick={download} className="mt-6 w-full bg-red-600 hover:bg-red-700 text-white h-11" data-testid="claim-download-btn">
                <Download className="mr-2 h-4 w-4" /> Download your certificate (PDF)
              </Button>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild variant="outline" data-testid="claim-cta-verify">
                <Link to={`/verify/${state.data.certificate_id}`}>
                  See public verification page <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <p className="text-xs text-zinc-500 mt-10 max-w-xl">
              Bookmark this link so you can re-download the PDF anytime. If you lose access, ask
              your compliance manager for the same URL again.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}
